import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/uyhu6wtg_WhatsApp%20Image%202026-07-05%20at%203.07.01%20PM%20%282%29.jpeg",
    alt: "REST. RESET. GO AGAIN. bedroom",
    caption: "REST. RESET. GO AGAIN. bedroom",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/jjn2ykar_WhatsApp%20Image%202026-07-05%20at%203.06.45%20PM%20%281%29.jpeg",
    alt: "DREAM HERE, GO ANYWHERE bedroom",
    caption: "DREAM HERE, GO ANYWHERE bedroom",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/1oi75m7c_WhatsApp%20Image%202026-07-05%20at%203.06.44%20PM.jpeg",
    alt: "Loka Beds room with smart TV",
    caption: "Loka Beds room with smart TV",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/94iyxsha_WhatsApp%20Image%202026-07-05%20at%203.06.50%20PM.jpeg",
    alt: "Minimalist wood work desk",
    caption: "Minimalist wood work desk",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/k4mu4gv1_WhatsApp%20Image%202026-07-05%20at%203.06.45%20PM.jpeg",
    alt: "Loka Beds shower",
    caption: "Loka Beds shower",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/7dujisy9_WhatsApp%20Image%202026-07-05%20at%203.07.00%20PM%20%282%29.jpeg",
    alt: "LokaBeds body wash and shampoo",
    caption: "LokaBeds body wash and shampoo",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/4xy60ngz_WhatsApp%20Image%202026-07-05%20at%203.07.00%20PM.jpeg",
    alt: "Loka Beds interior detail",
    caption: "Loka Beds interior detail",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/f7e996jx_WhatsApp%20Image%202026-07-05%20at%203.07.00%20PM%20%281%29.jpeg",
    alt: "Loka Beds mirror reflection",
    caption: "Loka Beds mirror reflection",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/y4m2fxnr_WhatsApp%20Image%202026-07-05%20at%203.06.46%20PM.jpeg",
    alt: "Loka Beds bathroom",
    caption: "Loka Beds bathroom",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/pabcueq4_WhatsApp%20Image%202026-07-05%20at%203.06.45%20PM%20%282%29.jpeg",
    alt: "LokaBeds amenities close-up",
    caption: "LokaBeds amenities close-up",
  },
];

const Gallery = () => {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(null);

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      setOpenIdx((i) => (i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length));
    },
    []
  );
  const next = useCallback(
    (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      setOpenIdx((i) => (i === null ? null : (i + 1) % IMAGES.length));
    },
    []
  );

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openIdx, close, prev, next]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 lg:py-32 bg-[#0b0c10]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-heading tracking-[0.3em] text-[#00FF66] uppercase mb-4">
              {t("gallery_kicker")}
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-white leading-[1.05]">
              {t("gallery_title")}
            </h2>
          </div>
          <p className="text-[#9CA3AF] max-w-md text-sm sm:text-base">
            {t("gallery_lead")}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {IMAGES.map((img, i) => {
            // Make certain items span more to create a nicer bento layout
            const spanClass =
              i === 0
                ? "col-span-2 row-span-2 aspect-square md:aspect-auto"
                : i === 5
                ? "col-span-2 aspect-[2/1]"
                : "aspect-square";
            return (
              <button
                key={img.src}
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setOpenIdx(i)}
                className={`gallery-item group relative overflow-hidden rounded-xl border border-white/5 hover:border-[rgba(0,255,102,0.5)] transition-all ${spanClass}`}
                aria-label={`Open ${img.alt}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c10]/85 via-[#0b0c10]/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2 translate-y-1 group-hover:translate-y-0 transition-transform">
                  <span className="text-[11px] sm:text-xs text-white/85 font-heading tracking-wide line-clamp-1">
                    {img.caption}
                  </span>
                  <span className="hidden sm:inline-flex text-[10px] text-[#00FF66] border border-[rgba(0,255,102,0.4)] px-2 py-0.5 rounded-full uppercase tracking-widest">
                    View
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {openIdx !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8 animate-fadeIn"
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          <button
            data-testid="gallery-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-[#0b0c10]/80 border border-[rgba(0,255,102,0.35)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.15)] flex items-center justify-center transition-all"
          >
            <X size={18} className="text-[#00FF66]" />
          </button>
          <button
            data-testid="gallery-prev"
            onClick={prev}
            aria-label="Previous"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0b0c10]/80 border border-[rgba(0,255,102,0.35)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.15)] flex items-center justify-center transition-all"
          >
            <ChevronLeft size={20} className="text-[#00FF66]" />
          </button>
          <button
            data-testid="gallery-next"
            onClick={next}
            aria-label="Next"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0b0c10]/80 border border-[rgba(0,255,102,0.35)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.15)] flex items-center justify-center transition-all"
          >
            <ChevronRight size={20} className="text-[#00FF66]" />
          </button>
          <div
            className="relative max-w-5xl w-full max-h-[85vh] flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={IMAGES[openIdx].src}
              alt={IMAGES[openIdx].alt}
              className="max-w-full max-h-[75vh] w-auto h-auto object-contain rounded-xl border border-[rgba(0,255,102,0.25)] shadow-[0_20px_80px_-20px_rgba(0,255,102,0.35)]"
            />
            <div className="flex items-center justify-between w-full text-xs sm:text-sm text-[#9CA3AF]">
              <span className="font-heading tracking-wide">{IMAGES[openIdx].caption}</span>
              <span className="text-[#00FF66] font-heading tracking-widest">
                {String(openIdx + 1).padStart(2, "0")} / {String(IMAGES.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
